import React from 'react';
import { styled } from '@mui/material';
import { IntervalTemplate } from 'api/report/types';
import BlockRoute from 'components/interface/BlockRoute/BlockRoute';
import TextArea from 'components/interface/textField/TextArea';
import { getIntervalDraftSummary } from 'report/logic/interval/calculations';
import { buildIntervalPayload } from 'report/logic/interval/payload';
import { ReportViewProps } from '../types';
import { useIntervalDraft } from './IntervalDraftContext';
import DesktopView from './desktop/View';
import MobileView from './mobile/View';
import { DayCards } from './shared/DayCards';
import { ReportSummary } from './shared/ReportSummary';
import { useIntervalEditor } from './useIntervalEditor';

const ReportForm = styled('form')({
    width: '100%',
    minWidth: 0,
});

const ReportContent = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const ReportOverview = styled(ReportContent)(({ theme }) => ({
    gap: theme.spacing(2),
}));

const ReportNotes = styled('div')(({ theme }) => ({
    minWidth: 0,
    paddingTop: theme.spacing(1),
}));

export default function Interval({
    isMobile,
    readOnly,
    disabled: interactionDisabled,
    loading,
    success,
    formID,
    onSubmit,
}: ReportViewProps): JSX.Element {
    const {
        sourceReport,
        draft,
        canAddEntry,
        setNotes,
        entryValidation,
        hasChanges,
    } = useIntervalDraft();
    const disabled = readOnly || interactionDisabled || loading;
    const editor = useIntervalEditor({ readOnly, disabled });
    const { entries, summary } = React.useMemo(() => {
        const entries = draft.days.flatMap(day => day.entries);
        const summary = getIntervalDraftSummary(entries, entryValidation);
        return { entries, summary };
    }, [draft.days, entryValidation]);
    const template = sourceReport.template.data as IntervalTemplate;

    const Layout = isMobile ? MobileView : DesktopView;

    return (
        <>
            <ReportForm
                id={formID}
                noValidate
                onSubmit={event => {
                    event.preventDefault();
                    if (disabled) return;
                    const payload = buildIntervalPayload(
                        sourceReport.template.id,
                        draft,
                        entryValidation
                    );
                    if (!payload) {
                        const firstInvalid = entries.find(entry => {
                            const validation = entryValidation[entry.uuid];
                            return (
                                !validation ||
                                Object.keys(validation.errors).length > 0
                            );
                        });
                        if (firstInvalid) {
                            editor.openEntry(firstInvalid.uuid);
                        }
                        return;
                    }
                    void onSubmit(payload);
                }}
                onKeyDown={event => {
                    if (
                        event.key === 'Enter' &&
                        event.target instanceof HTMLInputElement
                    ) {
                        event.preventDefault();
                    }
                }}>
                <Layout
                    editor={editor.content}
                    editorOpen={editor.isOpen}
                    onEditorExited={editor.onExited}>
                    <ReportContent>
                        <ReportOverview>
                            <DayCards
                                days={draft.days}
                                entryValidation={entryValidation}
                                entryKinds={template.entryKinds}
                                timeZone={draft.timeZone}
                                readOnly={readOnly}
                                disabled={disabled}
                                addDisabled={!canAddEntry}
                                selectedEntryID={
                                    editor.selectedEntry?.uuid ?? null
                                }
                                onAdd={editor.addEntry}
                                onSelect={editor.selectEntry}
                                isMobile={isMobile}
                            />
                            <ReportSummary
                                summary={summary}
                                entryKinds={template.entryKinds}
                            />
                        </ReportOverview>
                        <ReportNotes>
                            <TextArea
                                label='Kommentar'
                                value={draft.notes}
                                onChange={setNotes}
                                disabled={disabled}
                                fullWidth
                            />
                        </ReportNotes>
                    </ReportContent>
                </Layout>
            </ReportForm>
            <BlockRoute
                shouldBlockNavigation={!readOnly && !success && hasChanges}
                isMobile={isMobile}
            />
        </>
    );
}
